import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type MessageSummary } from "../../lib/api-client";
import { sanitizeHeaderText } from "../../lib/sanitize";

interface MessageListProps {
  mailboxId: string;
  selectedMessageId?: string;
  // refreshToken: archivar/eliminar sacan un correo del mailbox actual, hace
  // falta repedir la lista entera (no alcanza un patch local). Se incrementa
  // en MailLayout cada vez que eso pasa.
  refreshToken: number;
  // justSeenMessageId: en cambio, marcar leído sí se refleja con un patch
  // local barato — evita un round-trip solo para dejar de mostrar negrita.
  justSeenMessageId: string | null;
}

export default function MessageList({
  mailboxId,
  selectedMessageId,
  refreshToken,
  justSeenMessageId,
}: MessageListProps) {
  const [messages, setMessages] = useState<MessageSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMessages(null);
    api
      .messages(mailboxId, { limit: 50 })
      .then(setMessages)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxId, refreshToken]);

  useEffect(() => {
    if (!justSeenMessageId) return;
    setMessages(
      (prev) =>
        prev?.map((m) =>
          m.id === justSeenMessageId ? { ...m, keywords: { ...m.keywords, $seen: true } } : m,
        ) ?? prev,
    );
  }, [justSeenMessageId]);

  return (
    <div style={{ borderRight: "0.5px solid var(--border)", overflowY: "auto", minHeight: 0 }}>
      {error && <p style={{ padding: 16, fontSize: 13, color: "#b91c1c" }}>{error}</p>}
      {messages === null && !error && (
        <p style={{ padding: 16, fontSize: 13, color: "var(--text-secondary)" }}>Cargando…</p>
      )}
      {messages?.length === 0 && (
        <p style={{ padding: 16, fontSize: 13, color: "var(--text-secondary)" }}>
          No hay correos en este mailbox.
        </p>
      )}
      {messages?.map((msg) => {
        const isSelected = msg.id === selectedMessageId;
        const fromName = sanitizeHeaderText(msg.from?.[0]?.name || msg.from?.[0]?.email || "(sin remitente)");
        const subject = sanitizeHeaderText(msg.subject || "(sin asunto)");
        const isUnread = !msg.keywords?.["$seen"];

        return (
          <Link
            key={msg.id}
            to={`/mailbox/${mailboxId}/message/${msg.id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 16px",
              borderBottom: "0.5px solid var(--border)",
              borderLeft: isSelected ? "2px solid var(--color-primary)" : "2px solid transparent",
              background: isSelected ? "var(--color-selected-bg)" : "transparent",
              textDecoration: "none",
              color: "inherit",
              minWidth: 0,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: isUnread ? 600 : 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fromName}
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {subject}
              </p>
            </div>
            <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>
              {formatDate(msg.receivedAt)}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday
    ? date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}
